import { Component, OnInit } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { APP_AMOUNT_FORMAT } from '../../../../core/constants/app.constant';
import { sortNumberEnumConstantInterfaceAsc } from '../../../../core/libraries/helpers/enum-option-sort';
import {
    AISLE_WIDTH_OPTIONS,
    AisleWidthEnum,
    getAisleWidthOption,
    WORKING_ENVIRONMENTS_OPTIONS,
    WorkingEnvironmentEnum,
} from '../../../../features/admin/product/constants/product.constant';
import { IProductListFilter } from '../../../../features/admin/product/models/product.model';
import { ProductFilterService } from '../../../../features/admin/product/services/product-filter.service';

@Component({
    selector: 'app-search-recommender',
    standalone: false,
    templateUrl: './search-recommender.component.html',
    styleUrl: './search-recommender.component.scss',
})
export class SearchRecommenderComponent implements OnInit {
    protected form = this.formBuilder.group({
        minLoadCapacity: this.formBuilder.control<number | null>(null),
        maxLoadCapacity: this.formBuilder.control<number | null>(null),
        minLiftHeight: this.formBuilder.control<number | null>(null),
        maxLiftHeight: this.formBuilder.control<number | null>(null),
        workingEnvironments: [[] as WorkingEnvironmentEnum[]],
        aisleWidthCategory: [[] as AisleWidthEnum[]],
    });
    protected filterRanges!: Partial<IProductListFilter>;

    protected AISLE_WIDTH_OPTIONS = AISLE_WIDTH_OPTIONS;
    protected APP_AMOUNT_FORMAT = APP_AMOUNT_FORMAT;
    protected WORKING_ENVIRONMENTS_OPTIONS = WORKING_ENVIRONMENTS_OPTIONS;

    protected getAisleWidthOption = getAisleWidthOption;
    protected sortNumberEnumConstantInterfaceAsc = sortNumberEnumConstantInterfaceAsc;

    constructor(
        private dialogRef: MatDialogRef<SearchRecommenderComponent>,
        private formBuilder: NonNullableFormBuilder,
        private router: Router,
        private productFilterService: ProductFilterService,
    ) {}

    ngOnInit(): void {
        this.getFilterRanges();
    }

    protected onCancel(): void {
        this.dialogRef.close();
    }

    protected onSearch(): void {
        if (this.form.invalid) {
            return;
        }

        this.router.navigate(['/products'], {
            queryParams: this.buildQueryParams(this.form.value as IProductListFilter),
        });

        this.dialogRef.close();
    }

    protected toggleAisleWidth(width: AisleWidthEnum): void {
        const control = this.form.controls.aisleWidthCategory;
        const value = control.value;

        if (value.includes(width)) {
            control.setValue([]);
        } else {
            control.setValue([width]);
        }
    }

    protected toggleWorkingEnvironment(env: WorkingEnvironmentEnum): void {
        const control = this.form.controls.workingEnvironments;
        const value = control.value;

        const index = value.indexOf(env);

        if (index > -1) {
            control.setValue(value.filter((v) => v !== env));
            return;
        }

        control.setValue([...value, env]);
    }

    protected isAisleWidthSelected(width: AisleWidthEnum): boolean {
        return this.form.controls.aisleWidthCategory.value.includes(width);
    }

    protected isWorkingEnvironmentSelected(env: WorkingEnvironmentEnum): boolean {
        return this.form.controls.workingEnvironments.value.includes(env);
    }

    private buildQueryParams(value: IProductListFilter): Record<string, string | number | null> {
        return {
            minLoadCapacity: value.minLoadCapacity!,
            maxLoadCapacity: value.maxLoadCapacity!,
            minLiftHeight: value.minLiftHeight!,
            maxLiftHeight: value.maxLiftHeight!,
            workingEnvironments: value.workingEnvironments!.length ? value.workingEnvironments!.join(',') : null,
            aisleWidthCategory: value.aisleWidthCategory!.length ? value.aisleWidthCategory!.join(',') : null,
        };
    }

    private getFilterRanges(): void {
        this.productFilterService
            .getFilterRanges()
            .pipe(take(1))
            .subscribe((ranges) => {
                this.filterRanges = ranges;
                this.form.patchValue(ranges);
            });
    }
}
